
// =============================
// src/context/CarritoContext.jsx (actualizado con Envío + envío gratis a domicilio)
// =============================
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CarritoContext = createContext();

// Umbral de envío gratis (ARS)
const FREE_SHIPPING_THRESHOLD = 175000; // $175.000,00
const STORAGE_KEY = "wetCarritoCtx";

// --- Helpers ---
const normalizeQty = (it) =>
  Math.max(1, Number(it?.cantidad ?? it?.qty ?? it?.quantity ?? 1));

const normalizeItem = (it) => ({
  ...it,
  cantidad: normalizeQty(it),
});

const parsePrecio = (v) => {
  if (typeof v === "number") return v;
  if (v == null) return 0;
  // "$120.000,50" -> "120000.50"
  const s = String(v).replace(/[^\d.,-]/g, "");
  const normalized = s.replace(/\./g, "").replace(/,/g, ".");
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
};

export function CarritoProvider({ children }) {
  // Carga inicial desde localStorage (si existe)
  const initial = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  })();

  // ====== Carrito ======
  // Normalizamos para garantizar "cantidad"
  const [carrito, setCarrito] = useState(
    (initial?.carrito || []).map(normalizeItem)
  );

  // Mantengo tu API, pero ahora mergea y setea cantidad
  const agregarAlCarrito = (producto, talle, cantidad = 1) => {
    const qtyToAdd = Math.max(1, Number(cantidad || 1));

        // ✅ Meta Pixel: AddToCart (centralizado)
    const value = parsePrecio(producto?.precio) * qtyToAdd;
    if (window.fbq) {
      window.fbq("track", "AddToCart", {
        content_name: producto?.nombre,
        content_ids: [String(producto?.id ?? "")],
        content_type: "product",
        value: Number.isFinite(value) ? value : 0,
        currency: "ARS",
        quantity: qtyToAdd,
      });
    }
    
    setCarrito((prev) => {
      const idx = prev.findIndex(
        (it) => it.id === producto.id && (it.talle ?? "") === (talle ?? "")
      );
      if (idx >= 0) {
        const next = [...prev];
        const qPrev = normalizeQty(next[idx]);
        next[idx] = { ...next[idx], cantidad: qPrev + qtyToAdd };
        return next;
      }
      return [...prev, normalizeItem({ ...producto, talle, cantidad: qtyToAdd })];
    });
  };

  // Helpers opcionales (por si los usás en Drawer/Checkout)
  const setCantidad = (index, qty) => {
    const safe = Math.max(1, Number(qty || 1));
    setCarrito((prev) =>
      prev.map((it, i) => (i === index ? { ...it, cantidad: safe } : it))
    );
  };

  const eliminar = (index) => {
    setCarrito((prev) => prev.filter((_, i) => i !== index));
  };

  const getQty = (it) => normalizeQty(it);

  // Drawer (tal como lo tenías)
  const [isCartOpen, setIsCartOpen] = useState(false);
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen((v) => !v);

  // Subtotal: ahora considera cantidad × precio
  const subtotal = useMemo(() => {
    return carrito.reduce(
      (acc, item) => acc + parsePrecio(item.precio) * normalizeQty(item),
      0
    );
  }, [carrito]);

  // 🔔 Banner "agregado al carrito" (igual)
  const [isAddBannerOpen, setIsAddBannerOpen] = useState(false);
  const [addedItem, setAddedItem] = useState(null); // {img, nombre, talle, precio}
  const showAddBanner = (item) => {
    setAddedItem(item);
    setIsAddBannerOpen(true);
  };
  const hideAddBanner = () => setIsAddBannerOpen(false);

  // ====== Estado de Envío (compartido entre Carrito/Drawer/Checkout) ======
  const [provincia, setProvincia] = useState(initial?.envio?.provincia || "");
  const [cp, setCp] = useState(initial?.envio?.cp || ""); // CP/CPA
  const [metodoEntrega, setMetodoEntrega] = useState(
    initial?.envio?.metodoEntrega || "sucursal" // "domicilio" | "sucursal"
  );
  const [servicio, setServicio] = useState(
    initial?.envio?.servicio || "clasico"
  ); // "clasico" | "expreso"
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(
    initial?.envio?.sucursalSeleccionada || null
  );
  const [precioEnvio, setPrecioEnvio] = useState(
    typeof initial?.envio?.precioEnvio === "number"
      ? initial.envio.precioEnvio
      : null
  );

  // Reglas de coherencia básicas
  useEffect(() => {
    // Si cambia provincia: limpiar CP y sucursal
    setCp("");
    setSucursalSeleccionada(null);
  }, [provincia]);

  useEffect(() => {
    // Si cambia a domicilio: limpiar sucursal seleccionada
    if (metodoEntrega === "domicilio") setSucursalSeleccionada(null);
  }, [metodoEntrega]);

  // ====== DERIVADOS DE ENVÍO (reglas de negocio) ======
  const envioGratisElegible = useMemo(() => {
    return subtotal >= FREE_SHIPPING_THRESHOLD;
  }, [subtotal]);

  const precioEnvioFinal = useMemo(() => {
    if (metodoEntrega === "domicilio" && envioGratisElegible) return 0;
    return typeof precioEnvio === "number" ? precioEnvio : null;
  }, [metodoEntrega, envioGratisElegible, precioEnvio]);

  const total = useMemo(() => {
    const envio = typeof precioEnvioFinal === "number" ? precioEnvioFinal : 0;
    return subtotal + envio;
  }, [subtotal, precioEnvioFinal]);

  // Persistencia en localStorage (carrito + envío base)
  useEffect(() => {
    const snapshot = {
      carrito: carrito.map(normalizeItem), // nos aseguramos de persistir con cantidad
      envio: {
        provincia,
        cp,
        metodoEntrega,
        servicio,
        sucursalSeleccionada,
        precioEnvio,
      },
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (_) {}
  }, [
    carrito,
    provincia,
    cp,
    metodoEntrega,
    servicio,
    sucursalSeleccionada,
    precioEnvio,
  ]);

  return (
    <CarritoContext.Provider
      value={{
        // carrito
        carrito,
        setCarrito,
        agregarAlCarrito,
        setCantidad, // extra útil para Drawer
        eliminar,    // extra útil para Drawer
        subtotal,

        // drawer
        isCartOpen,
        openCart,
        closeCart,
        toggleCart,

        // banner
        isAddBannerOpen,
        addedItem,
        showAddBanner,
        hideAddBanner,

        // envío (estado base)
        provincia,
        setProvincia,
        cp,
        setCp,
        metodoEntrega,
        setMetodoEntrega,
        servicio,
        setServicio,
        sucursalSeleccionada,
        setSucursalSeleccionada,
        precioEnvio,
        setPrecioEnvio,

        // reglas / derivados
        FREE_SHIPPING_THRESHOLD,
        envioGratisElegible,
        precioEnvioFinal,
        total,

        // helpers
        getQty,
      }}
    >
      {children}
    </CarritoContext.Provider>
  );
}

export function useCarrito() {
  return useContext(CarritoContext);
}
